// frontend/src/components/BOQSummaryTab.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Divider,
  CircularProgress, Alert, ToggleButton, ToggleButtonGroup,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, IconButton, Collapse, Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  Download as DownloadIcon,
  Assignment as AssignmentIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const statusColors = { draft: 'default', approved: 'success', final: 'warning' };
const statusLabels = { draft: 'Draft', approved: 'Approved', final: 'Final' };

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const SiteBoqCard = ({ siteName, siteId, boqs, onDownload, userRole }) => {
  const [expanded, setExpanded] = useState(false);
  const [expandedBoqId, setExpandedBoqId] = useState(null);
  const [boqItems, setBoqItems] = useState({});
  const [loadingItems, setLoadingItems] = useState(false);
  const navigate = useNavigate();
  const finalBoq = boqs.find(b => b.status === 'final');
  const latestBoq = boqs[0];

  const handleToggleItems = async (boqId, source) => {
    if (expandedBoqId === boqId) {
      setExpandedBoqId(null);
      return;
    }
    setExpandedBoqId(boqId);
    if (boqItems[boqId]) return;
    if (source === 'multi') {
      setLoadingItems(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/boq-workbooks/${boqId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const wb = res.data.data.workbook;
        // Flatten all sheets cells into a displayable format
        const allSheets = wb.sheets || [];
        setBoqItems(prev => ({ ...prev, [boqId]: allSheets }));
      } catch (e) { console.error(e); }
      finally { setLoadingItems(false); }
      return;
    }
    setLoadingItems(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/boqs/${boqId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setBoqItems(prev => ({ ...prev, [boqId]: res.data.data.boq.items || [] }));
    } catch (e) { console.error(e); }
    finally { setLoadingItems(false); }
  };

  return (
    <Card elevation={2} sx={{ mb: 2, borderLeft: finalBoq ? '4px solid #f59e0b' : '4px solid #64A8ED' }}>
      <CardContent sx={{ pb: expanded ? 0 : '16px !important' }}>

        {/* Site Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AssignmentIcon color={finalBoq ? 'warning' : 'primary'} />
            <Box>
              <Typography variant="h6" fontWeight={700}>{siteName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {boqs.length} version{boqs.length !== 1 ? 's' : ''} • Final: {finalBoq?.versionLabel || 'None'}
                {finalBoq && <span style={{ color: '#f59e0b', fontWeight: 600 }}> • ⭐ Final BOQ exists</span>}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {finalBoq && (
              <Chip
                label={(() => {
                  if (finalBoq._source === 'multi') {
                    const monetarySheet = (finalBoq.sheets || []).find(s => s.totalUnit === '₹' && parseFloat(s.totalAmount) > 0);
                    return monetarySheet && userRole !== 'supervisor'
                      ? `Final: ${formatCurrency(parseFloat(monetarySheet.totalAmount))}`
                      : 'Final: Multi-Sheet BOQ';
                  }
                  return `Final: ${formatCurrency(finalBoq.totalAmount)}`;
                })()}
                color="warning" size="small" icon={<StarIcon />} />
            )}
            <Tooltip title="Open BOQ Management">
              <IconButton size="small" color="primary"
                onClick={() => navigate(`/boq?siteId=${siteId}&siteName=${encodeURIComponent(siteName)}`)}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Expanded: version table */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1.5 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.selected' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Version</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Uploaded By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Approved By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    {userRole !== 'supervisor' && <TableCell align="right" sx={{ fontWeight: 600 }}>Total Amount</TableCell>}
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boqs.filter(boq => boq.status === 'final').map((boq) => (
                    <React.Fragment key={boq.id}>
                      {/* BOQ version row */}
                      <TableRow hover sx={{ bgcolor: boq.status === 'final' ? '#fffde7' : 'transparent' }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {boq.status === 'final' && <StarIcon sx={{ fontSize: 14, color: '#f59e0b' }} />}
                            <Typography variant="body2" fontWeight={600}>{boq.versionLabel}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={statusLabels[boq.status]} color={statusColors[boq.status]} size="small" />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{boq.uploader?.name || '-'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={boq.approver ? 'success.main' : 'text.secondary'}>
                            {boq.approver ? `✅ ${boq.approver.name}` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(boq.createdAt || boq.created_at)}</Typography>
                        </TableCell>
                        {userRole !== 'supervisor' && (
                          <TableCell align="right">
                            {boq._source === 'multi' ? (
                              <Box>
                                {(() => {
                                  const monetarySheet = (boq.sheets || []).find(s => s.totalUnit === '₹' && parseFloat(s.totalAmount) > 0);
                                  return monetarySheet ? (
                                    <Typography variant="body2" fontWeight={600} color="primary.main">
                                      {formatCurrency(parseFloat(monetarySheet.totalAmount))}
                                    </Typography>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">—</Typography>
                                  );
                                })()}
                              </Box>
                            ) : (
                              <Typography variant="body2" fontWeight={600} color="primary.main">
                                {formatCurrency(boq.totalAmount)}
                              </Typography>
                            )}
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton size="small" color="info" onClick={() => handleToggleItems(boq.id, boq._source)}>
                              {expandedBoqId === boq.id ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          {boq._source === 'multi' && (
                            <Tooltip title="Open Full BOQ">
                              <IconButton size="small" color="primary" onClick={() => navigate(`/boq-workbook?siteId=${siteId}&siteName=${encodeURIComponent(siteName)}`)}>
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => onDownload(boq.id, boq.versionLabel, siteName)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>

                      {/* Line items sub-table */}
                      {expandedBoqId === boq.id && (
                        <TableRow>
                          <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                            <Collapse in={expandedBoqId === boq.id}>
                              <Box sx={{ bgcolor: 'background.default', px: 4, py: 1.5 }}>
                                {boq._source === 'multi' ? (
                                  loadingItems ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                      <CircularProgress size={20} />
                                    </Box>
                                  ) : (
                                    <Box>
                                      {(boqItems[boq.id] || []).map(sheet => {
                                        // Find Rate/Amount column indices to hide for supervisor
                                        const hiddenCols = new Set();
                                        if (userRole === 'supervisor' && sheet.cells) {
                                          const cellMap = {};
                                          sheet.cells.forEach(cell => {
                                            if (!cellMap[cell.rowIndex]) cellMap[cell.rowIndex] = {};
                                            cellMap[cell.rowIndex][cell.colIndex] = cell;
                                          });
                                          for (let r = 0; r <= 10; r++) {
                                            if (!cellMap[r]) continue;
                                            Object.entries(cellMap[r]).forEach(([colIdx, cell]) => {
                                              const v = String(cell.value || '').toLowerCase().trim();
                                              if (v.includes('rate') || v.includes('amount')) hiddenCols.add(parseInt(colIdx));
                                            });
                                          }
                                        }
                                        // Build grid
                                        const cells = sheet.cells || [];
                                        if (cells.length === 0) return null;
                                        let maxRow = 0, maxCol = 0;
                                        cells.forEach(c => {
                                          if (c.rowIndex > maxRow) maxRow = c.rowIndex;
                                          if (c.colIndex > maxCol) maxCol = c.colIndex;
                                        });
                                        const grid = Array.from({ length: maxRow + 1 }, () => Array(maxCol + 1).fill(null));
                                        cells.forEach(c => { grid[c.rowIndex][c.colIndex] = c; });
                                        const visibleCols = Array.from({ length: maxCol + 1 }, (_, i) => i).filter(i => !hiddenCols.has(i));

                                        return (
                                          <Box key={sheet.id} sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2" fontWeight={700} color="primary.main" sx={{ mb: 1 }}>
                                              {sheet.sheetName}
                                            </Typography>
                                            <Box sx={{ overflow: 'auto', maxHeight: '40vh' }}>
                                              <table style={{ borderCollapse: 'collapse', fontSize: '11px', fontFamily: 'Calibri, Arial, sans-serif', width: '100%' }}>
                                                <tbody>
                                                  {grid.map((row, rIdx) => (
                                                    <tr key={rIdx}>
                                                      {visibleCols.map(cIdx => {
                                                        const cell = row[cIdx];
                                                        const val = cell?.formattedValue ?? cell?.value ?? '';
                                                        return (
                                                          <td key={cIdx} style={{ border: '1px solid #e0e0e0', padding: '2px 6px', fontSize: '11px', whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 350, verticalAlign: 'top' }}>                                                            {String(val)}
                                                          </td>
                                                        );
                                                      })}
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </Box>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  )
                                ) : loadingItems ? (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                    <CircularProgress size={20} />
                                  </Box>
                                ) : (
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>S.No</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Description</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Unit</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Qty</TableCell>
                                        {userRole !== 'supervisor' && <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Rate (₹)</TableCell>}
                                        {userRole !== 'supervisor' && <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>Amount (₹)</TableCell>}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {(boqItems[boq.id] || []).map((item, idx) => (
                                        item.isHeader ? (
                                          <TableRow key={idx} sx={{ bgcolor: 'action.hover' }}>
                                            <TableCell colSpan={userRole === 'supervisor' ? 4 : 6}>
                                              <Typography variant="caption" fontWeight={700} color="primary.main">
                                                {item.description}
                                              </Typography>
                                            </TableCell>
                                          </TableRow>
                                        ) : (
                                          <TableRow key={idx} hover>
                                            <TableCell sx={{ fontSize: '0.75rem' }}>{item.sno}</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem' }}>{item.description}</TableCell>
                                            <TableCell sx={{ fontSize: '0.75rem' }}>{item.unit}</TableCell>
                                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{Number(item.quantity).toLocaleString()}</TableCell>
                                            {userRole !== 'supervisor' && <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{Number(item.rate).toLocaleString()}</TableCell>}
                                            {userRole !== 'supervisor' && <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>₹{Number(item.amount).toLocaleString()}</TableCell>}
                                          </TableRow>
                                        )
                                      ))}
                                      {userRole !== 'supervisor' && (
                                        <TableRow sx={{ bgcolor: 'action.selected' }}>
                                          <TableCell colSpan={5} align="right">
                                            <Typography variant="caption" fontWeight={700}>TOTAL</Typography>
                                          </TableCell>
                                          {userRole !== 'supervisor' && (
                                            <TableCell align="right">
                                              <Typography variant="caption" fontWeight={700} color="primary.main">
                                                {formatCurrency(boq.totalAmount)}
                                              </Typography>
                                            </TableCell>
                                          )}
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const BOQSummaryTab = ({ selectedSiteId, sites, userRole }) => {
  const [boqsBySite, setBoqsBySite] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [siteMode, setSiteMode] = useState('site');

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchBoqs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let url = `${API_BASE_URL}/boqs/unified-summary`;
      if (siteMode === 'site' && selectedSiteId) {
        url += `?siteId=${selectedSiteId}`;
      } else if (siteMode === 'site' && !selectedSiteId) {
        setBoqsBySite([]);
        setLoading(false);
        return;
      }
      const res = await axios.get(url, authHeaders());
      const boqs = res.data.data.boqs || [];
      const siteMap = {};
      boqs.forEach(boq => {
        const sid = boq.site?.id || boq.siteId;
        const sname = boq.site ? `${boq.site.siteName} — ${boq.site.location}` : 'Unknown Site';
        if (!siteMap[sid]) siteMap[sid] = { siteId: sid, siteName: sname, boqs: [] };
        siteMap[sid].boqs.push(boq);
      });
      setBoqsBySite(Object.values(siteMap));
    } catch (e) {
      setError('Failed to load BOQ summary');
    } finally {
      setLoading(false);
    }
  }, [selectedSiteId, siteMode]);

  useEffect(() => { fetchBoqs(); }, [fetchBoqs]);

  const handleDownload = async (boqId, versionLabel, siteName) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/boqs/${boqId}/download`, {
        ...authHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${versionLabel}_${siteName}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { setError('Download failed'); }
  };

  const totalFinalBoqs = boqsBySite.filter(s => s.boqs.some(b => b.status === 'final')).length;
  const totalVersions = boqsBySite.reduce((sum, s) => sum + s.boqs.length, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup value={siteMode} exclusive
            onChange={(e, val) => { if (val) setSiteMode(val); }} size="small">
            <ToggleButton value="site">This Site</ToggleButton>
            <ToggleButton value="all">All Sites</ToggleButton>
          </ToggleButtonGroup>
          {!loading && boqsBySite.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {boqsBySite.length} site{boqsBySite.length !== 1 ? 's' : ''} •{' '}
              {totalVersions} version{totalVersions !== 1 ? 's' : ''} •{' '}
              <strong>{totalFinalBoqs} finalized</strong>
            </Typography>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!selectedSiteId && siteMode === 'site' && (
        <Alert severity="info">Please select a site to view BOQ summary.</Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !error && boqsBySite.length === 0 && (selectedSiteId || siteMode === 'all') && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <AssignmentIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No BOQs found</Typography>
          <Typography variant="body2" color="text.disabled">
            Upload BOQs from the Sites page to see them here
          </Typography>
        </Box>
      )}

      {!loading && boqsBySite.map((site) => (
        <SiteBoqCard
          key={site.siteId}
          siteId={site.siteId}
          siteName={site.siteName}
          boqs={site.boqs}
          onDownload={handleDownload}
          userRole={userRole}
        />
      ))}
    </Box>
  );
};

export default BOQSummaryTab;